import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Filter } from 'lucide-react';
import { supabase, WeeklyLedger, LedgerEntry, EntryType } from '../lib/supabase';
import { formatDate } from '../lib/dateFormat';
import { useLanguage } from '../contexts/LanguageContext';
import { translateEntryTypeName } from '../lib/translateEntryType';
import StatementCard from '../components/StatementCard';

type LedgerWithDetails = WeeklyLedger & {
  entries: (LedgerEntry & { entry_type: EntryType })[];
};

export default function Statement() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [ledgers, setLedgers] = useState<LedgerWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all-time');

  useEffect(() => {
    loadLedgers();
  }, []);

  const loadLedgers = async () => {
    setLoading(true);
    const { data: ledgersData, error: ledgersError } = await supabase
      .from('weekly_ledgers')
      .select('*')
      .order('date', { ascending: false });

    if (ledgersError || !ledgersData) {
      setLoading(false);
      return;
    }

    const { data: entriesData } = await supabase
      .from('ledger_entries')
      .select('*, entry_type:entry_types(*)');

    const ledgersWithDetails = ledgersData.map(ledger => {
      const entries = entriesData?.filter(e => e.weekly_ledger_id === ledger.id) || [];
      return {
        ...ledger,
        entries
      };
    });

    setLedgers(ledgersWithDetails);
    setLoading(false);
  };

  const applyQuickFilter = (filter: string) => {
    setActiveFilter(filter);
    const now = new Date();
    let start: Date | null = null;
    let end: Date = now;

    switch (filter) {
      case 'this-month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this-year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'last-year':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'all-time':
      default:
        setStartDate('');
        setEndDate('');
        return;
    }

    if (start) {
      setStartDate(start.toISOString().split('T')[0]);
    }
    setEndDate(end.toISOString().split('T')[0]);
  };

  const clearFilter = () => {
    setStartDate('');
    setEndDate('');
    setActiveFilter('all-time');
  };

  const filteredLedgers = ledgers.filter(ledger => {
    const ledgerDate = new Date(ledger.date);

    if (startDate && ledgerDate < new Date(startDate)) {
      return false;
    }

    if (endDate && ledgerDate > new Date(endDate)) {
      return false;
    }

    return true;
  });

  const totalIncome = filteredLedgers.reduce((sum, ledger) => {
    const income = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).entry_category === 'income')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + income;
  }, 0);

  const totalExpenses = filteredLedgers.reduce((sum, ledger) => {
    const expenses = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).entry_category === 'expense')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + expenses;
  }, 0);

  const totalTithe = filteredLedgers.reduce((sum, ledger) => {
    const tithe = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).name === 'tithe')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + tithe;
  }, 0);

  const totalSabbathSchool = filteredLedgers.reduce((sum, ledger) => {
    const sabbathSchool = ledger.entries
      .filter(e => (e.entry_type as unknown as EntryType).name === 'Sabbath School')
      .reduce((acc, e) => acc + Number(e.amount), 0);
    return sum + sabbathSchool;
  }, 0);

  const topEntryTypes = filteredLedgers
    .flatMap(ledger => ledger.entries)
    .reduce((acc, entry) => {
      const entryType = entry.entry_type as unknown as EntryType;
      const key = entryType.name;
      if (!acc[key]) {
        acc[key] = {
          name: translateEntryTypeName(key, language),
          category: entryType.entry_category,
          total: 0
        };
      }
      acc[key].total += Number(entry.amount);
      return acc;
    }, {} as Record<string, { name: string; category: string; total: number }>);

  const topIncomeTypes = Object.values(topEntryTypes)
    .filter(type => type.category === 'income')
    .sort((a, b) => b.total - a.total);

  const topExpenseTypes = Object.values(topEntryTypes)
    .filter(type => type.category === 'expense')
    .sort((a, b) => b.total - a.total);

  const quickFilters = [
    { key: 'all-time', label: t('statement.allTime') },
    { key: 'this-month', label: t('statement.thisMonth') },
    { key: 'last-month', label: t('statement.lastMonth') },
    { key: 'this-year', label: t('statement.thisYear') },
    { key: 'last-year', label: t('statement.lastYear') }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">{t('statement.title')}</h1>
        <p className="text-slate-600 mt-1">{t('statement.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Filter className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">{t('statement.dateRange')}</h2>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {quickFilters.map(filter => (
              <button
                key={filter.key}
                onClick={() => applyQuickFilter(filter.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeFilter === filter.key
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {t('statement.startDate')}
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {t('statement.endDate')}
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setActiveFilter('custom');
                  }}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {(startDate || endDate) && activeFilter !== 'all-time' && (
            <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <span className="font-semibold">{t('statement.showing')}:</span>{' '}
                {startDate ? formatDate(startDate, 'MMM d, yyyy', language) : t('statement.allTime')}{' '}
                {t('statement.to')}{' '}
                {endDate ? formatDate(endDate, 'MMM d, yyyy', language) : formatDate(new Date().toISOString(), 'MMM d, yyyy', language)}
              </p>
              <button
                onClick={clearFilter}
                className="px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 rounded-lg transition-colors"
              >
                {t('statement.clearFilter')}
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-600">{t('common.loading')}</div>
      ) : (
        <StatementCard
          incomeTypes={topIncomeTypes}
          expenseTypes={topExpenseTypes}
          totalIncome={totalIncome}
          totalExpenses={totalExpenses}
          totalTithe={totalTithe}
          totalSabbathSchool={totalSabbathSchool}
        />
      )}
    </div>
  );
}
